"use client";

import { FC, useMemo, useRef } from "react";
import { z } from "zod";
import { cn } from "@/lib/utils";
// [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → now central widget_impression
// (TrackedWidget in NewPage); see webapp/docs/analytics
// import { FireWidgetViewedEvent } from "@/customAnalytics/trackingEvents";
import { formatBackgroundStyle } from "@/utils/styles/format";
import { useIsMobile } from "@/hooks/useIsMobile";
import RevealOnScroll from "@/components/atoms/RevealOnScroll";

/**
 * Section copy caps (enforced here in the widget, NOT in the CMS):
 * - Section heading: max 2 lines (CSS line-clamp).
 * - Section description: max 4 lines (CSS line-clamp).
 * - Skills (chips): max 4 lines (CSS max-height clip) + max 30 chars per skill.
 */
const INSTITUTES1_LIMITS = {
  skillChars: 30,
} as const;

function clampStr(s: string, max: number) {
  return s.length <= max ? s : s.slice(0, max);
}

/** One skill chip (institution category). */
const SkillSchema = z.object({
  id: z.union([z.number(), z.string()]),
  meta_title: z
    .string()
    .optional()
    .default("")
    .transform((s) => clampStr(String(s ?? "").trim(), INSTITUTES1_LIMITS.skillChars)),
  meta_priority: z.coerce.number().optional().default(0),
});

type Skill = z.infer<typeof SkillSchema>;

const Institutes1DataSchema = z
  .object({
    // Heading line 1 (white).
    pre_title: z.string().optional().default("Graduates build careers across"),
    pre_title_color: z.string().optional().default("#FFFFFF"),
    // Heading line 2 (highlight, italic).
    title: z.string().optional().default("leading financial institutions"),
    title_color: z.string().optional().default("#02A7B6"),
    description: z.string().optional().default(""),
    description_color: z.string().optional().default("rgba(255,255,255,0.7)"),
    desktop_background_color: z.string().optional().default("#000000"),
    mobile_background_color: z.string().optional().default("#000000"),
    backvariant: z
      .enum(["variant_1", "variant_2", "variant_3"])
      .optional()
      .default("variant_3"),
    // Highlight font (Denton Test -> Merriweather per the font rule).
    font_style: z.string().optional().default("var(--font-merriweather)"),
    gradient_color_1: z.string().optional().default(""),
    gradient_color_2: z.string().optional().default(""),
    gradient_color_3: z.string().optional().default(""),
    gradient_color_4: z.string().optional().default(""),
    pill_background_color: z.string().optional().default("rgba(86,226,245,0.2)"),
    pill_text_color: z.string().optional().default("#FFFFFF"),
    pill_border_color: z.string().optional().default("rgba(255,255,255,0.2)"),
    /** MySQL / API may send 0/1; coerce to boolean. */
    enable_animation: z
      .preprocess((v) => v === true || v === 1 || v === "1", z.boolean())
      .optional()
      .default(false),
    metadata: z.array(SkillSchema).optional().default([]),
  })
  .transform((data) => ({
    ...data,
    // Order chips by priority (ascending).
    metadata: [...(data.metadata ?? [])].sort(
      (a, b) => (a.meta_priority || 0) - (b.meta_priority || 0),
    ),
  }));

type Institutes1Data = z.infer<typeof Institutes1DataSchema>;

/** One skill chip. */
const SkillPill: FC<{ skill: Skill; data: Institutes1Data }> = ({ skill, data }) => (
  <span
    className={cn(
      "flex items-center justify-center shrink-0 whitespace-nowrap font-medium leading-[1.4] border-[0.6px] border-solid",
      // Radius per breakpoint (exact from Figma)
      "rounded-[30px] sm:rounded-[22.5px] md:rounded-[25.625px] lg:rounded-[30px] xl:rounded-[30px]",
      // Padding per breakpoint
      "px-[20px] py-[10px] sm:px-[15px] sm:py-[8px] md:px-[16px] md:py-[8px] lg:px-[20px] lg:py-[10px] xl:px-[20px] xl:py-[10px]",
      // Text size per breakpoint
      "text-[14px] sm:text-[12px] md:text-[13px] lg:text-[16px] xl:text-[16px]",
      // Letter spacing per breakpoint
      "tracking-[0.14px] sm:tracking-[0.12px] md:tracking-[0.13px] lg:tracking-[0.16px] xl:tracking-[0.16px]",
    )}
    style={{
      backgroundColor: data.pill_background_color,
      borderColor: data.pill_border_color,
      color: data.pill_text_color,
      fontFamily: "var(--font-google-sans)",
    }}
  >
    {skill.meta_title}
  </span>
);

export const Institutes1: FC<any> = (props) => {
  // Props can be { data } or { data: { data } } (admin preview) — extract the real data.
  const actualData = props?.data?.data || props?.data || props;

  const sectionRef = useRef<HTMLElement>(null);
  const isMobile = useIsMobile();

  const parseResult = useMemo(
    () => Institutes1DataSchema.safeParse(actualData),
    [actualData],
  );

  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (sectionRef.current) {
  //     FireWidgetViewedEvent(sectionRef, "leading_financial_institutions");
  //   }
  // }, []);

  if (!parseResult.success) {
    console.error("❌ Institutes1 Zod validation failed:", parseResult.error);
    return null;
  }

  const parsedData: Institutes1Data = parseResult.data;
  const isAnimationEnabled = parsedData.enable_animation;
  const skills = parsedData.metadata.filter((s) => s.meta_title);

  const bgColor = formatBackgroundStyle(parsedData, isMobile || false, "#000000");

  return (
    <section
      ref={sectionRef}
      className={cn(
        "relative w-full overflow-hidden flex justify-center",
        // Section padding per breakpoint (exact from Figma)
        "xs:px-[24px] xs:py-[40px]",
        "sm:px-[60px] sm:py-[40px]",
        "md:px-[60px] md:py-[60px]",
        "lg:px-[80px] lg:py-[60px]",
        "xl:px-[140px] xl:py-[80px]",
      )}
      style={bgColor}
    >
      <div
        className={cn(
          "relative z-10 w-full",
          "sm:w-[960px] md:w-[1110px] lg:w-[1270px] xl:w-[1470px]",
        )}
      >
        <div
          className={cn(
            "flex w-full",
            // Mobile: stacked (text over chips); Desktop: text-left / chips-right
            "flex-col gap-[16px]",
            "sm:flex-row sm:items-center sm:justify-between sm:gap-[24px]",
          )}
        >
          {/* Text column */}
          <div
            className={cn(
              "flex flex-col items-start w-full",
              "gap-[16px] sm:gap-[18px] md:gap-[20px] lg:gap-[24px] xl:gap-[24px]",
              "sm:w-[396px] md:w-[451px] lg:w-[528px] xl:w-[600px]",
            )}
          >
            {/* Section heading (max 2 lines): white line 1 + highlighted italic line 2 */}
            <RevealOnScroll enableAnimation={isAnimationEnabled}>
              <p
                className={cn(
                  "line-clamp-2 leading-[1.4] [word-break:break-word]",
                  "text-[24px] sm:text-[20px] md:text-[24px] lg:text-[28px] xl:text-[28px]",
                  "tracking-[0.24px] sm:tracking-[0.2px] md:tracking-[0.24px] lg:tracking-[0.28px] xl:tracking-[0.28px]",
                )}
              >
                <span
                  className="font-semibold"
                  style={{
                    color: parsedData.pre_title_color,
                    fontFamily: "var(--font-google-sans)",
                  }}
                >
                  {parsedData.pre_title}
                </span>{" "}
                <span
                  className="italic font-extrabold"
                  style={{
                    color: parsedData.title_color,
                    fontFamily: parsedData.font_style,
                  }}
                >
                  {parsedData.title}
                </span>
              </p>
            </RevealOnScroll>

            {/* Section description (max 4 lines) */}
            {parsedData.description ? (
              <RevealOnScroll enableAnimation={isAnimationEnabled}>
                <p
                  className={cn(
                    "line-clamp-4 font-semibold leading-[1.49] [word-break:break-word]",
                    "text-[14px] lg:text-[18px] xl:text-[18px]",
                  )}
                  style={{
                    color: parsedData.description_color,
                    fontFamily: "var(--font-google-sans)",
                  }}
                >
                  {parsedData.description}
                </p>
              </RevealOnScroll>
            ) : null}
          </div>

          {/* Skill chips — flex-wrap, max 4 lines (overflow clipped) */}
          {skills.length > 0 ? (
            <div
              className={cn(
                "w-full",
                "sm:w-[545px] md:w-[620px] lg:w-[727px] xl:w-[727px]",
              )}
            >
              <RevealOnScroll enableAnimation={isAnimationEnabled}>
                <div
                  className={cn(
                    "flex flex-wrap content-center items-center justify-center overflow-hidden w-full",
                    "gap-y-[14px] gap-x-[14px] sm:gap-x-[12px] md:gap-x-[12px] lg:gap-x-[14px] xl:gap-x-[14px]",
                    // Max 4 lines of chips per breakpoint
                    "max-h-[210px] sm:max-h-[185px] md:max-h-[190px] lg:max-h-[222px] xl:max-h-[222px]",
                  )}
                >
                  {skills.map((skill) => (
                    <SkillPill key={skill.id} skill={skill} data={parsedData} />
                  ))}
                </div>
              </RevealOnScroll>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
};

export default Institutes1;
